import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { contactApi } from "@/api";
import type {
  ContactMessageFilter,
  CreateContactMessageData,
  UpdateContactMessageData,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

// ─── Ommaviy forma ────────────────────────────────────────────────────────────

export const useSubmitContactMessage = () => {
  return useMutation({
    mutationFn: (data: CreateContactMessageData) => contactApi.submit(data),
  });
};

// ─── Moderator / admin pochtasi ──────────────────────────────────────────────

export const useContactMessages = (filter: ContactMessageFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CONTACT_MESSAGES, filter],
    queryFn: () => contactApi.list(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useContactStats = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CONTACT_STATS],
    queryFn: () => contactApi.getStats(),
    select: res => res.data,
  });

export const useContactMessage = (id: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CONTACT_MESSAGE, id],
    queryFn: () => contactApi.getOne(id as number),
    enabled: !!id,
    select: res => res.data,
  });

const useInvalidateContact = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CONTACT_MESSAGES] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CONTACT_MESSAGE] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CONTACT_STATS] });
  };
};

export const useUpdateContactMessage = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateContact();

  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateContactMessageData }) =>
      contactApi.update(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteContactMessage = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateContact();

  return useMutation({
    mutationFn: (id: number) => contactApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.DELETED"));
    },
  });
};
